import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Rocket, Trophy, Users, Code, Calendar, MapPin, ArrowRight, Search, 
  Zap, Shield, Star, ChevronRight, Globe, TrendingUp,
  Terminal, Sparkles, ArrowUpRight, Menu, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { hackathonAPI, authAPI } from '@/lib/api';
import { isAuthenticated, setAuth } from '@/lib/auth';
import AuthModal from '@/components/AuthModal';
import Footer from '@/components/Footer';
import { getHackathonBanner } from '@/lib/bannerImages';
import SEO from '@/components/SEO';

// Animated Counter Component
const AnimatedCounter = ({ end, duration = 2000, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (countRef.current) observer.observe(countRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return <span ref={countRef}>{count.toLocaleString()}{suffix}</span>;
};

// Premium Hackathon Card
const HackathonCard = ({ hackathon, onClick, index }) => {
  const getStatusConfig = (hack) => {
    const now = new Date();
    const regEnd = new Date(hack.registration_end);
    const eventStart = new Date(hack.event_start);
    const eventEnd = new Date(hack.event_end);

    if (now < regEnd) return { label: 'Open', class: 'status-live' };
    if (now >= eventStart && now <= eventEnd) return { label: 'Live', class: 'status-live' };
    if (now > eventEnd) return { label: 'Ended', class: 'status-ended' };
    return { label: 'Soon', class: 'status-upcoming' };
  };

  const status = getStatusConfig(hackathon);
  const eventStart = new Date(hackathon.event_start);
  const bannerUrl = getHackathonBanner(hackathon);

  return (
    <Card 
      className="group relative overflow-hidden bg-white border border-gray-200 rounded-xl hover:border-teal-400 transition-all duration-300 cursor-pointer shadow-sm hover:shadow-lg"
      onClick={onClick}
      style={{ animationDelay: `${index * 0.1}s` }}
      data-testid={`hackathon-card-${hackathon.slug}`}
    >
      {/* Glow Effect on Hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/0 to-cyan-500/0 group-hover:from-emerald-500/5 group-hover:to-cyan-500/5 transition-all duration-500" />
      
      {/* Image */}
      <div className="relative h-40 overflow-hidden bg-gray-100">
        <img 
          src={bannerUrl} 
          alt={hackathon.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = 'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=1200&h=400&fit=crop';
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent" />
        
        <div className="absolute top-3 left-3">
          <Badge className={`status-badge ${status.class}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
            {status.label}
          </Badge>
        </div>

        {hackathon.prize_pool && (
          <div className="absolute top-3 right-3">
            <Badge className="bg-amber-500/90 text-black font-mono text-xs px-2 py-1">
              ₹{hackathon.prize_pool?.toLocaleString()}
            </Badge>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-5 space-y-3">
        <h3 className="font-heading font-semibold text-lg text-gray-900 line-clamp-1 group-hover:text-teal-600 transition-colors">
          {hackathon.title}
        </h3>
        
        <p className="text-gray-600 text-sm line-clamp-2">
          {hackathon.tagline || hackathon.description?.substring(0, 80)}
        </p>

        <div className="flex items-center gap-4 text-xs text-gray-500 font-mono">
          <div className="flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 text-teal-600" />
            {eventStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </div>
          <div className="flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5 text-teal-600" />
            <span className="capitalize">{hackathon.location || 'Online'}</span>
          </div>
        </div>

        <div className="pt-3 border-t border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <Users className="w-3.5 h-3.5" />
            {hackathon.registration_count || 0} joined
          </div>
          <span className="text-teal-600 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
            View
            <ArrowUpRight className="w-3.5 h-3.5" />
          </span>
        </div>
      </div>
    </Card>
  );
};

// Feature Card for Bento Grid - Enhanced Version
const FeatureCard = ({ icon: Icon, title, description, className = '', gradient, size = 'default', accentColor }) => {
  const sizeClasses = {
    default: 'p-6',
    large: 'p-8 md:col-span-2',
    tall: 'p-6 md:row-span-2'
  };
  
  return (
    <div className={`group relative overflow-hidden rounded-2xl transition-all duration-500 hover:scale-[1.02] ${sizeClasses[size]} ${className}`}
      style={{
        background: `linear-gradient(135deg, ${accentColor}08 0%, ${accentColor}15 100%)`,
        border: `1px solid ${accentColor}20`
      }}
    >
      {/* Animated Background Glow */}
      <div 
        className="absolute -top-20 -right-20 w-40 h-40 rounded-full blur-3xl opacity-0 group-hover:opacity-40 transition-opacity duration-700"
        style={{ background: accentColor }}
      />
      
      {/* Floating Particles */}
      <div className="absolute top-4 right-4 w-2 h-2 rounded-full opacity-20 group-hover:opacity-60 transition-all duration-500 group-hover:scale-150"
        style={{ background: accentColor }}
      />
      <div className="absolute bottom-8 right-8 w-1 h-1 rounded-full opacity-10 group-hover:opacity-40 transition-all duration-700 group-hover:translate-y-[-10px]"
        style={{ background: accentColor }}
      />
      
      {/* Icon Container */}
      <div 
        className="relative w-14 h-14 rounded-xl flex items-center justify-center mb-5 group-hover:rotate-3 group-hover:scale-110 transition-all duration-300"
        style={{ 
          background: `linear-gradient(135deg, ${accentColor} 0%, ${accentColor}dd 100%)`,
          boxShadow: `0 10px 30px -10px ${accentColor}60`
        }}
      >
        <Icon className="w-7 h-7 text-white" />
      </div>
      
      {/* Content */}
      <h3 className="font-heading font-bold text-xl mb-2 text-gray-900 group-hover:text-gray-800 transition-colors">
        {title}
      </h3>
      <p className="text-gray-600 text-sm leading-relaxed">
        {description}
      </p>
      
      {/* Bottom Accent Line */}
      <div 
        className="absolute bottom-0 left-0 h-1 w-0 group-hover:w-full transition-all duration-500"
        style={{ background: `linear-gradient(90deg, ${accentColor} 0%, transparent 100%)` }}
      />
    </div>
  );
};

// Testimonial Card
const testimonials = [
  {
    quote: "Hackov8 stands out because it actually solves the real pain points of hackathons. From smooth team collaboration to transparent judging and effortless event management, everything feels thoughtfully designed. It's a platform built by someone who understands the hackathon ecosystem end to end.",
    author: "Rohan Sharma",
    role: "DevRel, devEco",
    avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Rohan"
  },
  {
    quote: "Found my co-founders here. Now we're a funded startup. The networking is incredible.",
    author: "Rahul Kumar",
    role: "Founder, TechStart",
    avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Rahul"
  },
  {
    quote: "The best hackathon platform I've used. Clean interface, powerful features.",
    author: "Sneha Patel",
    role: "3x Hackathon Winner",
    avatar: "https://api.dicebear.com/7.x/avataaars/svg?seed=Sneha"
  }
];

export default function LandingEnhanced() {
  const navigate = useNavigate();
  const [hackathons, setHackathons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  useEffect(() => {
    // Ensure light mode only
    document.documentElement.classList.remove('dark');
  }, []);

  useEffect(() => {
    const fragment = window.location.hash;
    if (fragment && fragment.includes('session_id=')) {
      handleAuthCallback(fragment);
      return;
    }
    fetchHackathons();
  }, []);

  const handleAuthCallback = async (fragment) => {
    const sessionId = fragment.split('session_id=')[1];
    if (sessionId) {
      try {
        const response = await authAPI.processSession(sessionId);
        const userData = response.data;
        setAuth(userData, userData.session_token);
        window.location.hash = '';
        
        if (userData.role === 'admin') navigate('/admin');
        else if (userData.role === 'organizer') navigate('/organizer/dashboard');
        else navigate('/dashboard');
        
        toast.success(`Welcome back, ${userData.name}!`);
      } catch (error) {
        toast.error('Authentication failed');
      }
    }
  };

  const fetchHackathons = async () => {
    setLoading(true);
    try {
      const response = await hackathonAPI.getAll({ status: 'published' });
      setHackathons(response.data);
    } catch (error) {
      console.error('Error fetching hackathons:', error);
    }
    setLoading(false);
  };

  const filteredHackathons = hackathons.filter(h =>
    h.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    h.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleGetStarted = () => {
    if (isAuthenticated()) {
      navigate('/dashboard');
    } else {
      setShowAuthModal(true);
    }
  };

  return (
    <>
      <SEO 
        title="Hackov8 - Where Ideas Become Reality" 
        description="Join India's premier hackathon platform. Participate, build, and win at cutting-edge hackathons."
      />
      
      <div className="min-h-screen bg-background text-foreground">
        
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 glass-effect border-b border-border/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
            <div className="flex justify-between items-center h-16 sm:h-20 md:h-28 lg:h-32">
              {/* Logo with Gradient Glow */}
              <div className="flex items-center gap-3 cursor-pointer group" onClick={() => navigate('/')}>
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 rounded-xl blur-lg opacity-0 group-hover:opacity-60 transition-opacity duration-300"></div>
                  <img 
                    src="https://customer-assets.emergentagent.com/job_50845b9f-14b0-452c-b665-fa440207483a/artifacts/opizci2q_hack.png" 
                    alt="Hackov8" 
                    className="relative h-12 sm:h-16 md:h-24 lg:h-28 xl:h-32 w-auto drop-shadow-lg group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
              </div>

              {/* Nav Links - Desktop */}
              <div className="hidden md:flex items-center gap-8">
                <a href="#hackathons" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                  Hackathons
                </a>
                <a href="#features" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                  Features
                </a>
                <a href="#community" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                  Community
                </a>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3">
                {isAuthenticated() ? (
                  <Button 
                    onClick={() => navigate('/dashboard')}
                    className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg font-medium hidden md:flex"
                    data-testid="dashboard-btn"
                  >
                    Dashboard
                  </Button>
                ) : (
                  <Button 
                    onClick={handleGetStarted}
                    className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white rounded-lg font-medium shadow-lg shadow-blue-500/20 hidden md:flex"
                    data-testid="get-started-btn"
                  >
                    Get Started
                  </Button>
                )}
                
                {/* Mobile Menu Button */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="md:hidden rounded-lg"
                  onClick={() => setShowMobileMenu(!showMobileMenu)}
                  data-testid="mobile-menu-btn"
                >
                  {showMobileMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </Button>
              </div>
            </div>
            
            {/* Mobile Menu Dropdown */}
            {showMobileMenu && (
              <div className="md:hidden py-4 border-t border-border space-y-2 animate-fadeIn">
                <a 
                  href="#hackathons" 
                  className="block py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => setShowMobileMenu(false)}
                >
                  Hackathons
                </a>
                <a 
                  href="#features" 
                  className="block py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => setShowMobileMenu(false)}
                >
                  Features
                </a>
                <a 
                  href="#community" 
                  className="block py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => setShowMobileMenu(false)}
                >
                  Community
                </a>
                <div className="flex items-center gap-3 pt-3 border-t border-border">
                  {isAuthenticated() ? (
                    <Button 
                      onClick={() => { navigate('/dashboard'); setShowMobileMenu(false); }}
                      className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg font-medium flex-1"
                    >
                      Dashboard
                    </Button>
                  ) : (
                    <Button 
                      onClick={() => { handleGetStarted(); setShowMobileMenu(false); }}
                      className="bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-lg font-medium flex-1"
                    >
                      Get Started
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </nav>

        {/* Hero Section - Split Layout */}
        <section className="relative pt-28 sm:pt-32 pb-12 sm:pb-16 md:pt-36 md:pb-24 overflow-hidden">
          {/* Background Effects */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
            <div className="absolute inset-0 bg-[linear-gradient(rgba(59,130,246,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(59,130,246,0.03)_1px,transparent_1px)] bg-[size:60px_60px]" />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
              {/* Left Content */}
              <div className="space-y-6 sm:space-y-8">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20">
                  <Sparkles className="w-4 h-4 text-primary" />
                  <span className="text-xs sm:text-sm font-medium text-primary">India's #1 Hackathon Platform</span>
                </div>

                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold leading-[1.1] tracking-tight">
                  Where <span className="gradient-text">Ideas</span> Become{' '}
                  <span className="gradient-text">Reality</span>
                </h1>

                <p className="text-base sm:text-lg text-muted-foreground max-w-lg leading-relaxed">
                  Join thousands of developers, designers, and innovators. Build groundbreaking projects, compete for prizes, and accelerate your career.
                </p>

                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                  <button 
                    onClick={handleGetStarted}
                    className="group relative overflow-hidden rounded-full px-8 sm:px-12 h-14 sm:h-16 text-base sm:text-lg font-bold shadow-2xl hover:scale-105 transition-all duration-300 flex items-center justify-center text-white"
                    style={{
                      background: 'linear-gradient(135deg, #10b981 0%, #14b8a6 50%, #06b6d4 100%)',
                      boxShadow: '0 20px 40px -10px rgba(16, 185, 129, 0.5)'
                    }}
                    data-testid="hero-cta-join"
                  >
                    <Zap className="w-5 h-5 sm:w-6 sm:h-6 mr-3" style={{ color: 'white' }} />
                    <span style={{ color: 'white' }}>Join a Hackathon</span>
                    <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 ml-2 group-hover:translate-x-1 transition-transform" style={{ color: 'white' }} />
                  </button>
                  <button 
                    onClick={() => setShowAuthModal(true)}
                    className="rounded-full px-6 sm:px-8 h-14 sm:h-16 text-base sm:text-lg font-semibold border-2 border-gray-300 hover:border-teal-400 text-gray-700 transition-all duration-300 flex items-center justify-center gap-2"
                    style={{ backgroundColor: 'white' }}
                    data-testid="hero-cta-host"
                  >
                    <span>Host an Event</span>
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>

                {/* Stats Row - Mobile Responsive */}
                <div className="grid grid-cols-3 gap-4 sm:flex sm:items-center sm:gap-8 pt-6">
                  <div className="text-center sm:text-left">
                    <div className="text-xl sm:text-3xl font-heading font-bold text-foreground">
                      <AnimatedCounter end={50000} suffix="+" />
                    </div>
                    <div className="text-xs sm:text-sm text-muted-foreground font-mono">Participants</div>
                  </div>
                  <div className="hidden sm:block w-px h-12 bg-border" />
                  <div className="text-center sm:text-left">
                    <div className="text-xl sm:text-3xl font-heading font-bold text-foreground">
                      <AnimatedCounter end={500} suffix="+" />
                    </div>
                    <div className="text-xs sm:text-sm text-muted-foreground font-mono">Hackathons</div>
                  </div>
                  <div className="hidden sm:block w-px h-12 bg-border" />
                  <div className="text-center sm:text-left">
                    <div className="text-xl sm:text-3xl font-heading font-bold text-foreground">
                      ₹<AnimatedCounter end={50} suffix="L+" />
                    </div>
                    <div className="text-xs sm:text-sm text-muted-foreground font-mono">In Prizes</div>
                  </div>
                </div>
              </div>

              {/* Right Visual - Terminal Style Card */}
              <div className="relative hidden lg:block">
                <div className="relative bg-card border border-border rounded-xl overflow-hidden shadow-2xl">
                  {/* Terminal Header */}
                  <div className="flex items-center gap-2 px-4 py-3 bg-muted/50 border-b border-border">
                    <div className="flex gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-red-500" />
                      <div className="w-3 h-3 rounded-full bg-yellow-500" />
                      <div className="w-3 h-3 rounded-full bg-green-500" />
                    </div>
                    <span className="text-xs text-muted-foreground font-mono ml-2">hackov8.terminal</span>
                  </div>
                  {/* Terminal Content */}
                  <div className="p-6 font-mono text-sm space-y-3">
                    <div className="flex items-start gap-2">
                      <span className="text-primary">$</span>
                      <span className="text-foreground">hackov8 join --hackathon "AI Innovation"</span>
                    </div>
                    <div className="text-muted-foreground pl-4">
                      ✓ Registration confirmed
                    </div>
                    <div className="text-muted-foreground pl-4">
                      ✓ Team created: "Code Wizards"
                    </div>
                    <div className="text-muted-foreground pl-4">
                      ✓ Project initialized
                    </div>
                    <div className="flex items-start gap-2 pt-2">
                      <span className="text-primary">$</span>
                      <span className="text-foreground">hackov8 submit --project "SmartHealth AI"</span>
                    </div>
                    <div className="text-emerald-400 pl-4 flex items-center gap-2">
                      <span className="animate-pulse">●</span>
                      Submission successful! Good luck! 🚀
                    </div>
                    <div className="flex items-start gap-2 pt-2">
                      <span className="text-primary">$</span>
                      <span className="text-foreground animate-pulse">_</span>
                    </div>
                  </div>
                </div>
                
                {/* Floating Badge */}
                <div className="absolute -top-4 -right-4 bg-gradient-to-br from-amber-500 to-orange-500 text-black px-4 py-2 rounded-lg font-semibold text-sm shadow-lg animate-float">
                  🏆 ₹10L+ in Prizes
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Hackathons Section */}
        <section id="hackathons" className="py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            {/* Section Header */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
              <div>
                <Badge className="status-badge status-live mb-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
                  Live Now
                </Badge>
                <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-2">
                  Active Hackathons
                </h2>
                <p className="text-muted-foreground">
                  Discover and join the most exciting challenges
                </p>
              </div>
              
              {/* Search */}
              <div className="relative w-full md:w-80">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search hackathons..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-11 pr-4 h-11 rounded-lg bg-muted/50 border-border"
                  data-testid="search-hackathons"
                />
              </div>
            </div>

            {/* Hackathon Grid */}
            {loading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse rounded-xl bg-muted h-80" />
                ))}
              </div>
            ) : filteredHackathons.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredHackathons.slice(0, 6).map((hackathon, index) => (
                  <HackathonCard
                    key={hackathon.id}
                    hackathon={hackathon}
                    index={index}
                    onClick={() => navigate(`/hackathon/${hackathon.slug}`)}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-16 rounded-xl bg-muted/30 border border-border">
                <Rocket className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold mb-2">No hackathons found</h3>
                <p className="text-muted-foreground">Check back soon for upcoming events!</p>
              </div>
            )}

            {filteredHackathons.length > 6 && (
              <div className="text-center mt-10">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    const hackathonsSection = document.getElementById('hackathons');
                    if (hackathonsSection) {
                      hackathonsSection.scrollIntoView({ behavior: 'smooth' });
                    } else {
                      navigate('/#hackathons');
                    }
                  }}
                  className="rounded-lg"
                  data-testid="view-all-hackathons"
                >
                  View All Hackathons
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* Features Section - Creative Bento Grid */}
        <section id="features" className="py-20 md:py-32 relative overflow-hidden">
          {/* Background Elements */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-gray-50 via-white to-gray-50" />
            <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-200/30 rounded-full blur-3xl" />
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-200/20 rounded-full blur-3xl" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-teal-100/20 to-blue-100/20 rounded-full blur-3xl" />
          </div>
          
          <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            {/* Section Header */}
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-emerald-100 to-cyan-100 border border-emerald-200/50 mb-6">
                <Terminal className="w-4 h-4 text-emerald-600" />
                <span className="text-sm font-semibold text-emerald-700">Powerful Features</span>
              </div>
              <h2 className="text-4xl sm:text-5xl font-heading font-bold mb-4 text-gray-900">
                Built for <span className="bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 bg-clip-text text-transparent">Builders</span>
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto text-lg">
                Everything you need to participate, organize, and win at hackathons
              </p>
            </div>

            {/* Creative Bento Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {/* Large Feature - Team Formation */}
              <div className="lg:col-span-2 lg:row-span-2 group relative overflow-hidden rounded-3xl p-8 transition-all duration-500 hover:scale-[1.01]"
                style={{
                  background: 'linear-gradient(135deg, #10b98110 0%, #14b8a620 100%)',
                  border: '1px solid #10b98130'
                }}
              >
                <div className="absolute -top-32 -right-32 w-64 h-64 bg-emerald-400/20 rounded-full blur-3xl group-hover:bg-emerald-400/40 transition-all duration-700" />
                <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-500 to-teal-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                
                <div className="relative">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center mb-6 shadow-xl shadow-emerald-500/30 group-hover:rotate-6 group-hover:scale-110 transition-all duration-300">
                    <Users className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Team Formation</h3>
                  <p className="text-gray-600 mb-6 max-w-md">Find your dream teammates with our AI-powered matching system. Connect based on skills, interests, and goals.</p>
                  
                  {/* Mini Avatar Stack */}
                  <div className="flex items-center gap-3">
                    <div className="flex -space-x-3">
                      {[1,2,3,4].map(i => (
                        <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-teal-400 border-2 border-white flex items-center justify-center text-white text-xs font-bold shadow-lg">
                          {String.fromCharCode(64 + i)}
                        </div>
                      ))}
                    </div>
                    <span className="text-sm text-gray-500">+2,000 teams formed</span>
                  </div>
                </div>
              </div>

              {/* Trophy Card */}
              <FeatureCard 
                icon={Trophy} 
                title="Live Leaderboards" 
                description="Track rankings in real-time and compete with the best."
                accentColor="#f59e0b"
              />
              
              {/* Code Card */}
              <FeatureCard 
                icon={Code} 
                title="Project Submissions" 
                description="Easy submission with GitHub integration."
                accentColor="#06b6d4"
              />

              {/* Wide Feature - Real-time Updates */}
              <div className="lg:col-span-2 group relative overflow-hidden rounded-3xl p-6 transition-all duration-500 hover:scale-[1.01]"
                style={{
                  background: 'linear-gradient(135deg, #8b5cf610 0%, #a855f720 100%)',
                  border: '1px solid #8b5cf630'
                }}
              >
                <div className="absolute -bottom-20 -right-20 w-40 h-40 bg-purple-400/20 rounded-full blur-3xl group-hover:bg-purple-400/40 transition-all duration-700" />
                
                <div className="relative flex items-start gap-5">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-xl shadow-purple-500/30 group-hover:rotate-6 transition-all duration-300 flex-shrink-0">
                    <Zap className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Real-time Updates</h3>
                    <p className="text-gray-600 text-sm">Get instant notifications about hackathon updates, announcements, and when your teammates are online.</p>
                  </div>
                  
                  {/* Animated Notification Dots */}
                  <div className="absolute top-4 right-4 flex gap-1">
                    <span className="w-2 h-2 bg-purple-500 rounded-full animate-pulse" />
                    <span className="w-2 h-2 bg-pink-400 rounded-full animate-pulse delay-100" />
                    <span className="w-2 h-2 bg-purple-400 rounded-full animate-pulse delay-200" />
                  </div>
                </div>
              </div>

              {/* Shield Card */}
              <FeatureCard 
                icon={Shield} 
                title="Secure Platform" 
                description="Enterprise-grade security for your projects."
                accentColor="#64748b"
              />
              
              {/* DevMetric Card - Large Feature */}
              <div className="lg:col-span-2 group relative overflow-hidden rounded-3xl p-6 transition-all duration-500 hover:scale-[1.01]"
                style={{
                  background: 'linear-gradient(135deg, #14b8a610 0%, #06b6d420 100%)',
                  border: '1px solid #14b8a630'
                }}
              >
                <div className="absolute -top-20 -right-20 w-40 h-40 bg-teal-400/20 rounded-full blur-3xl group-hover:bg-teal-400/40 transition-all duration-700" />
                
                <div className="relative flex items-start gap-5">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center shadow-xl shadow-teal-500/30 group-hover:rotate-6 transition-all duration-300 flex-shrink-0">
                    <TrendingUp className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">DevMetric Score</h3>
                    <p className="text-gray-600 text-sm mb-4">Track your hackathon journey with our unique scoring system. Earn points for events, projects, and teams. Share your achievements!</p>
                    
                    {/* Level Badges */}
                    <div className="flex flex-wrap gap-2">
                      <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold">🎯 Newbie</span>
                      <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold">🌱 Rising</span>
                      <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold">⚡ Pro</span>
                      <span className="px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-semibold">🔥 Expert</span>
                      <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">👑 Legend</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Stats Bar - Now more prominent */}
            <div className="mt-16 relative">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/20 via-cyan-500/20 to-purple-500/20 rounded-3xl blur-xl" />
              <div className="relative p-8 rounded-3xl bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 border border-gray-700/50 shadow-2xl">
                {/* Animated gradient border */}
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-emerald-500 via-cyan-500 to-purple-500 opacity-20 blur-sm" />
                
                <div className="relative grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                  <div className="group">
                    <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-emerald-400 to-green-300 bg-clip-text text-transparent mb-3 group-hover:scale-110 transition-transform">99.9%</div>
                    <div className="text-emerald-300 text-base font-bold tracking-wide">Uptime Guaranteed</div>
                  </div>
                  <div className="group">
                    <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent mb-3 group-hover:scale-110 transition-transform">2M+</div>
                    <div className="text-teal-300 text-base font-bold tracking-wide">Lines of Code</div>
                  </div>
                  <div className="group">
                    <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent mb-3 group-hover:scale-110 transition-transform">500+</div>
                    <div className="text-cyan-300 text-base font-bold tracking-wide">Hackathons Hosted</div>
                  </div>
                  <div className="group">
                    <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent mb-3 group-hover:scale-110 transition-transform">24/7</div>
                    <div className="text-purple-300 text-base font-bold tracking-wide">Support Available</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section id="community" className="py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-3">
                Loved by <span className="gradient-text">Builders</span>
              </h2>
              <p className="text-muted-foreground">
                Hear from our community of innovators
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {testimonials.map((testimonial, idx) => (
                <Card key={idx} className="p-6 bg-card border border-border rounded-xl hover:border-primary/30 transition-colors">
                  <div className="mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 inline text-amber-500 fill-amber-500" />
                    ))}
                  </div>
                  <p className="text-foreground mb-6 leading-relaxed">"{testimonial.quote}"</p>
                  <div className="flex items-center gap-3">
                    <img 
                      src={testimonial.avatar} 
                      alt={testimonial.author}
                      className="w-10 h-10 rounded-full bg-muted"
                    />
                    <div>
                      <div className="font-semibold text-foreground text-sm">{testimonial.author}</div>
                      <div className="text-xs text-muted-foreground">{testimonial.role}</div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section - Premium Vibrant Design */}
        <section className="py-20 md:py-32 relative overflow-hidden">
          {/* Background */}
          <div className="absolute inset-0 bg-gradient-to-b from-white via-emerald-50/30 to-cyan-50/30" />
          
          <div className="relative max-w-6xl mx-auto px-6 md:px-12 lg:px-24">
            {/* Main CTA Card */}
            <div className="relative rounded-[2.5rem] overflow-hidden">
              {/* Vibrant Gradient Background */}
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-700" />
              
              {/* Animated Mesh Gradient Orbs - More vibrant */}
              <div className="absolute top-0 left-0 w-96 h-96 bg-emerald-400/40 rounded-full blur-[100px] animate-pulse" />
              <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-300/40 rounded-full blur-[100px] animate-pulse delay-1000" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-teal-400/30 rounded-full blur-[80px]" />
              <div className="absolute top-1/4 right-1/4 w-48 h-48 bg-yellow-400/20 rounded-full blur-[60px]" />
              
              {/* Grid Pattern Overlay */}
              <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.08)_1px,transparent_1px)] bg-[size:40px_40px]" />
              
              {/* Floating Elements - More visible */}
              <div className="absolute top-10 left-10 w-20 h-20 border-2 border-white/20 rounded-2xl rotate-12 hidden md:block" />
              <div className="absolute bottom-10 right-10 w-16 h-16 border-2 border-white/20 rounded-full hidden md:block" />
              <div className="absolute top-20 right-32 w-8 h-8 bg-white/10 rounded-lg rotate-45 hidden md:block" />
              <div className="absolute bottom-20 left-32 w-6 h-6 bg-yellow-400/30 rounded-full hidden md:block" />
              <div className="absolute top-1/4 right-20 w-4 h-4 bg-white rounded-full animate-ping hidden md:block" />
              <div className="absolute bottom-1/4 left-20 w-3 h-3 bg-yellow-300 rounded-full animate-ping delay-500 hidden md:block" />
              
              {/* Content */}
              <div className="relative px-8 py-16 md:px-16 md:py-24 text-center">
                {/* Badge */}
                <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 mb-8 shadow-lg">
                  <Sparkles className="w-5 h-5 text-yellow-300" />
                  <span className="text-sm font-bold text-white">Join 50,000+ Builders</span>
                </div>
                
                {/* Heading */}
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-6 leading-tight drop-shadow-lg">
                  Ready to Build<br />
                  <span className="text-yellow-300 drop-shadow-md">
                    Something Amazing?
                  </span>
                </h2>
                
                {/* Subtext */}
                <p className="text-lg md:text-xl text-white/90 mb-10 max-w-2xl mx-auto leading-relaxed">
                  Start your hackathon journey today. Build projects, win prizes, and connect with developers worldwide.
                </p>
                
                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <button 
                    onClick={handleGetStarted}
                    className="group relative px-10 py-5 rounded-2xl font-bold text-lg overflow-hidden transition-all duration-300 hover:scale-105 bg-white text-emerald-700 shadow-2xl hover:shadow-white/30"
                    data-testid="cta-get-started"
                  >
                    <span className="absolute inset-0 bg-gradient-to-r from-yellow-200/0 via-yellow-200/50 to-yellow-200/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                    <span className="relative flex items-center gap-3">
                      <Rocket className="w-6 h-6" />
                      Get Started Free
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </button>
                  
                  <button 
                    onClick={() => {
                    const hackathonsSection = document.getElementById('hackathons');
                    if (hackathonsSection) {
                      hackathonsSection.scrollIntoView({ behavior: 'smooth' });
                    } else {
                      navigate('/#hackathons');
                    }
                  }}
                    className="px-8 py-5 rounded-2xl font-bold text-lg bg-white text-teal-700 hover:bg-yellow-300 hover:text-gray-900 transition-all duration-300 flex items-center gap-2 shadow-xl"
                  >
                    <Globe className="w-5 h-5" />
                    Explore Hackathons
                  </button>
                </div>
                
                {/* Trust Indicators */}
                <div className="mt-12 pt-8 border-t border-white/20">
                  <div className="flex flex-wrap items-center justify-center gap-8 text-white/90 text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-yellow-300" />
                      <span>Secure & Encrypted</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Zap className="w-5 h-5 text-yellow-300" />
                      <span>Instant Setup</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-yellow-300" />
                      <span>Active Community</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <Footer />

        {/* Auth Modal */}
        {showAuthModal && (
          <AuthModal 
            isOpen={showAuthModal} 
            onClose={() => setShowAuthModal(false)}
            onSuccess={(data) => {
              setShowAuthModal(false);
              if (data.role === 'admin') navigate('/admin');
              else if (data.role === 'organizer') navigate('/organizer/dashboard');
              else navigate('/dashboard');
            }}
          />
        )}
      </div>
    </>
  );
}
